import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Image as ImageIcon, X, Camera, ChevronLeft, ChevronRight } from 'lucide-react';
interface RequestBriefProps {
  prompt?: string;
  serviceName?: string;
  photos?: string[];
  /** Compact variant for use inside checkout / activity panels */
  compact?: boolean;
  /** Allow user to add more photos inline (Booking summary uses this) */
  onAddPhoto?: () => void;
}
export function RequestBrief({
  prompt,
  serviceName,
  photos = [],
  compact = false,
  onAddPhoto
}: RequestBriefProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  if (!prompt && photos.length === 0) return null;
  const openLightbox = (i: number) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () => setLightboxIndex(i => i === null ? null : (i - 1 + photos.length) % photos.length);
  const next = () => setLightboxIndex(i => i === null ? null : (i + 1) % photos.length);
  return <>
      <div className={`bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden ${compact ? '' : ''}`} data-id="element-573">
        {/* Header */}
        <div className={`px-4 py-3 flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-brand-50/40 to-white ${compact ? 'py-2.5' : ''}`} data-id="element-574">
          <div className="flex items-center gap-2 min-w-0" data-id="element-575">
            <div className="w-7 h-7 rounded-lg orange-gradient-bg flex items-center justify-center shrink-0" data-id="element-576">
              <Sparkles className="w-3.5 h-3.5 text-white" data-id="element-577" />
            </div>
            <div className="min-w-0" data-id="element-578">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 leading-tight" data-id="element-579">
                Your request
              </p>
              {serviceName && <p className="text-xs font-semibold text-gray-900 leading-tight truncate" data-id="element-580">
                  {serviceName}
                </p>}
            </div>
          </div>
          {photos.length > 0 && <div className="flex items-center gap-1 text-[10px] font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full shrink-0" data-id="element-581">
              <ImageIcon className="w-3 h-3" data-id="element-582" />
              {photos.length}
            </div>}
        </div>

        {/* Body */}
        <div className="p-4" data-id="element-583">
          {prompt && <p className={`text-sm italic text-gray-700 leading-relaxed ${photos.length ? 'mb-3' : ''}`} data-id="element-584">
              "{prompt}"
            </p>}

          {/* Photo strip — compact horizontal row, scales for many photos */}
          {(photos.length > 0 || onAddPhoto) && (() => {
          const thumb = compact ? 'w-10 h-10' : 'w-12 h-12';
          const VISIBLE = compact ? 5 : 6;
          const hasOverflow = photos.length > VISIBLE;
          const shown = hasOverflow ? photos.slice(0, VISIBLE - 1) : photos;
          return <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 -mx-0.5 px-0.5" data-id="element-585">
                  {shown.map((src, i) => <button key={`${src}-${i}`} onClick={() => openLightbox(i)} className={`${thumb} relative rounded-lg overflow-hidden border border-gray-100 bg-gray-100 hover:ring-2 hover:ring-brand-400 transition-all shrink-0`} title={`Photo ${i + 1} of ${photos.length}`} data-id="element-586">
                      <img src={src} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" data-id="element-587" />
                    </button>)}
                  {hasOverflow && <button onClick={() => openLightbox(VISIBLE - 1)} className={`${thumb} relative rounded-lg overflow-hidden border border-gray-100 bg-gray-900 hover:bg-black transition-colors shrink-0 flex items-center justify-center`} title={`View all ${photos.length} photos`} data-id="element-588">
                      <img src={photos[VISIBLE - 1]} alt={`Upload ${VISIBLE}`} className="w-full h-full object-cover opacity-40" data-id="element-589" />
                      <span className="absolute inset-0 flex items-center justify-center text-white text-[11px] font-bold" data-id="element-590">
                        +{photos.length - (VISIBLE - 1)}
                      </span>
                    </button>}
                  {onAddPhoto && <button onClick={onAddPhoto} className={`${thumb} rounded-lg border-2 border-dashed border-gray-200 hover:border-brand-400 bg-gray-50 hover:bg-brand-50 flex items-center justify-center text-gray-400 hover:text-brand-600 transition-colors shrink-0`} title="Add photo" data-id="element-591">
                      <Camera className="w-3.5 h-3.5" data-id="element-592" />
                    </button>}
                </div>;
        })()}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-593">
        {lightboxIndex !== null && photos[lightboxIndex] && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6" onClick={closeLightbox} data-id="element-594">
            <button onClick={e => {
          e.stopPropagation();
          closeLightbox();
        }} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center text-white" aria-label="Close" data-id="element-595">
              <X className="w-5 h-5" data-id="element-596" />
            </button>
            <p className="absolute top-6 left-6 text-white/80 font-semibold text-xs uppercase tracking-wider" data-id="element-597">
              Photo {lightboxIndex + 1} / {photos.length}
            </p>
            {photos.length > 1 && <>
                <button onClick={e => {
            e.stopPropagation();
            prev();
          }} className="absolute left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center text-white" aria-label="Previous" data-id="element-598">
                  <ChevronLeft className="w-5 h-5" data-id="element-599" />
                </button>
                <button onClick={e => {
            e.stopPropagation();
            next();
          }} className="absolute right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center text-white" aria-label="Next" data-id="element-600">
                  <ChevronRight className="w-5 h-5" data-id="element-601" />
                </button>
              </>}
            <motion.img key={lightboxIndex} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} src={photos[lightboxIndex]} alt={`Upload ${lightboxIndex + 1}`} onClick={e => e.stopPropagation()} className="max-w-full max-h-full rounded-2xl object-contain shadow-2xl" data-id="element-602" />
          </motion.div>}
      </AnimatePresence>
    </>;
}